import { AxiosError, AxiosResponse } from 'axios';

// Handles API calls that were redirected to the unavailable page while Mobius
// was already open. In that case we want one clean page redirect, not a normal
// in-app error message.
const MAINTENANCE_REDIRECT_URL = 'https://getmoremath.com/unavailable/';
const MAINTENANCE_UNAVAILABLE_PATH = '/unavailable';
const MAINTENANCE_REDIRECT_STORAGE_KEY = 'gmmMaintenanceRedirectedAt';
const MAINTENANCE_REDIRECT_LOOP_GUARD_MS = 30_000;
const MAINTENANCE_RESOURCE_LOOKBACK_MS = 10_000;
const MOBIUS_API_PATH_PREFIXES = ['/api/v2', '/vo/v1'];
const TRUSTED_MAINTENANCE_ORIGINS = [new URL(MAINTENANCE_REDIRECT_URL).origin];

// Remembers that this browser tab has already started a maintenance redirect.
let maintenanceRedirectStarted = false;

type MaintenanceRedirectCandidate = AxiosResponse | AxiosError | undefined;

// Gets session storage from the browser when it is available.
const getBrowserSessionStorage = (): Storage | undefined => {
  if (typeof window === 'undefined') return undefined;

  try {
    // Some browsers can block storage access, so read it defensively.
    return window.sessionStorage;
  } catch {
    return undefined;
  }
};

// Reads the last redirect time from session storage. If storage is missing or
// contains something odd, treat it like no previous redirect happened.
const getRecentRedirectTimestamp = (): number | null => {
  try {
    const redirectTimestamp = getBrowserSessionStorage()?.getItem(
      MAINTENANCE_REDIRECT_STORAGE_KEY,
    );

    if (!redirectTimestamp) return null;

    const timestamp = Number(redirectTimestamp);

    return Number.isFinite(timestamp) ? timestamp : null;
  } catch {
    return null;
  }
};

// Checks whether another request already started a redirect very recently.
const hasRecentRedirect = (): boolean => {
  // A recent redirect means another API call already handled maintenance mode.
  const timestamp = getRecentRedirectTimestamp();

  return (
    timestamp !== null &&
    Date.now() - timestamp < MAINTENANCE_REDIRECT_LOOP_GUARD_MS
  );
};

// Records that this tab has started a maintenance redirect.
const setRecentRedirectTimestamp = (): void => {
  try {
    // Save a short-lived guard so repeated API calls do not fight over redirecting.
    getBrowserSessionStorage()?.setItem(
      MAINTENANCE_REDIRECT_STORAGE_KEY,
      String(Date.now()),
    );
  } catch {
    // The in-memory flag still guards the current runtime when storage is unavailable.
  }
};

// Checks one Axios result. If it came from the unavailable page, start the
// browser redirect and tell the caller that maintenance mode was handled here.
export const maybeRedirectForMaintenanceResponse = (
  response?: MaintenanceRedirectCandidate,
): boolean => {
  if (typeof window === 'undefined') return false;

  const maintenanceResponseUrl = getMaintenanceResponseUrl(response);

  if (!maintenanceResponseUrl) return false;

  // If we are already on the unavailable page, there is no extra navigation to do.
  if (isMaintenancePageUrl(window.location.href)) return true;

  // Multiple requests can finish at almost the same time. Only the first one
  // should try to change the page.
  if (maintenanceRedirectStarted || hasRecentRedirect()) return true;

  maintenanceRedirectStarted = true;
  setRecentRedirectTimestamp();

  try {
    // CORS can block JavaScript from reading the redirected API response, but
    // it does not block full-page navigation once we know the trusted URL.
    window.location.replace(maintenanceResponseUrl);
  } catch {
    // If navigation is blocked for some reason, undo the guard and let the
    // original Axios response/error continue through the normal app code.
    maintenanceRedirectStarted = false;

    try {
      getBrowserSessionStorage()?.removeItem(MAINTENANCE_REDIRECT_STORAGE_KEY);
    } catch {
      // The original Axios response or error can continue when navigation is blocked.
    }

    return false;
  }

  return true;
};

// Returns a promise that never settles while the browser is leaving Mobius.
export const waitForMaintenanceNavigation = <T>(): Promise<T> =>
  new Promise<T>(() => {
    // Intentionally unresolved. The unavailable page should replace this app
    // before any caller needs a success value or an error value.
  });

// Finds the final URL that Axios saw after any browser redirects.
const getMaintenanceResponseUrl = (
  result?: MaintenanceRedirectCandidate,
): string | undefined => {
  if (!result) return undefined;

  // Axios stores the final URL on the browser request object. That URL matters
  // more than the original API path because the load balancer may have redirected.
  const responseUrl =
    getResponseUrl(getAxiosResponse(result)?.request) ||
    getResponseUrl(isAxiosError(result) ? result.request : undefined);

  if (responseUrl && isTrustedMaintenancePageUrl(responseUrl)) {
    return responseUrl;
  }

  // When CORS blocks the redirected XHR, Axios may only expose a network error.
  // In that case, use the browser's recent resource history as the missing clue.
  return isOpaqueApiError(result)
    ? getRecentMaintenanceResourceUrl()
    : undefined;
};

// Pulls the normal response object out of either a success or error result.
const getAxiosResponse = (
  result: AxiosResponse | AxiosError,
): AxiosResponse | undefined =>
  // Axios errors sometimes still include a response object.
  isAxiosError(result) ? result.response : result;

// Checks the marker Axios puts on its own error objects.
const isAxiosError = (
  result: AxiosResponse | AxiosError,
): result is AxiosError => 'isAxiosError' in result && result.isAxiosError;

// Reads the browser's final request URL when Axios exposes it.
const getResponseUrl = (request: unknown): string | undefined =>
  // Browser XHR requests expose the final URL here after redirects.
  (request as { responseURL?: string } | undefined)?.responseURL;

// Checks for the CORS-blocked shape: an API call failed without a readable
// response, but the browser may still have recorded the redirected resource.
const isOpaqueApiError = (result: AxiosResponse | AxiosError): boolean => {
  if (!isAxiosError(result) || result.response) return false;

  const requestStatus = getRequestStatus(result.request);

  if (typeof requestStatus === 'number' && requestStatus !== 0) return false;

  return isMobiusApiRequest(result);
};

// Reads the XHR status when the browser exposes it. CORS-blocked requests often
// surface as status 0.
const getRequestStatus = (request: unknown): number | undefined =>
  (request as { status?: number } | undefined)?.status;

// Decides whether the original Axios config points at one of our API paths.
const isMobiusApiRequest = (error: AxiosError): boolean => {
  const requestPath = getAxiosRequestPath(error);

  return (
    !!requestPath &&
    MOBIUS_API_PATH_PREFIXES.some(prefix => requestPath.startsWith(prefix))
  );
};

// Rebuilds the path Axios will request from baseURL and url.
const getAxiosRequestPath = (error: AxiosError): string | undefined => {
  const { baseURL, url } = error.config ?? {};

  if (!url) return undefined;

  if (isAbsoluteUrl(url)) return parseUrl(url)?.pathname;

  const basePath = baseURL
    ? parseUrl(baseURL, getCurrentLocationHref())?.pathname
    : '';
  const urlPath = parseUrl(url, getCurrentLocationHref())?.pathname;

  if (!urlPath) return undefined;

  return normalizeJoinedPath(basePath, urlPath);
};

// Absolute URLs should not be joined to Axios baseURL.
const isAbsoluteUrl = (url: string): boolean =>
  /^[a-z][a-z\d+\-.]*:/i.test(url);

// Joins paths like /vo/v1 + /teachers/1 into /vo/v1/teachers/1.
const normalizeJoinedPath = (
  basePath: string | undefined,
  urlPath: string,
): string => {
  const cleanedBasePath = (basePath ?? '').replace(/\/+$/, '');
  const cleanedUrlPath = urlPath.replace(/^\/+/, '');

  return `${cleanedBasePath}/${cleanedUrlPath}`.replace(/\/+/g, '/');
};

// Finds a very recent XHR/fetch for a trusted unavailable page. The browser can
// record this resource even when JavaScript cannot read the response body.
const getRecentMaintenanceResourceUrl = (): string | undefined => {
  if (typeof performance === 'undefined') return undefined;

  const getEntriesByType = performance.getEntriesByType?.bind(performance);

  if (!getEntriesByType) return undefined;

  const now =
    typeof performance.now === 'function' ? performance.now() : undefined;
  const resources = getEntriesByType('resource');

  for (let index = resources.length - 1; index >= 0; index -= 1) {
    const resource = resources[index] as PerformanceResourceTiming;

    if (!isRecentResource(resource, now)) continue;
    if (!isApiFetchResource(resource)) continue;
    if (isTrustedMaintenancePageUrl(resource.name)) return resource.name;
  }

  return undefined;
};

// Avoid using stale resource entries from earlier browsing.
const isRecentResource = (
  resource: PerformanceResourceTiming,
  now: number | undefined,
): boolean =>
  now === undefined ||
  now - resource.startTime <= MAINTENANCE_RESOURCE_LOOKBACK_MS;

// The maintenance resource we care about is the XHR/fetch redirect target.
const isApiFetchResource = (resource: PerformanceResourceTiming): boolean =>
  resource.initiatorType === 'xmlhttprequest' ||
  resource.initiatorType === 'fetch';

// Checks whether any URL path is exactly the unavailable page path.
const isMaintenancePageUrl = (url: string): boolean => {
  // Used for the current page URL, where the origin does not matter.
  const parsedUrl = parseUrl(url);

  return !!parsedUrl && hasMaintenancePath(parsedUrl);
};

// Checks whether the final response URL is one we are willing to redirect to.
const isTrustedMaintenancePageUrl = (url: string): boolean => {
  // Only trust the exact unavailable page on getmoremath.com, or an unavailable
  // page served from the same origin as the current app.
  const parsedUrl = parseUrl(url, getCurrentLocationHref());

  if (!parsedUrl || !hasMaintenancePath(parsedUrl)) return false;

  return (
    parsedUrl.origin === getCurrentOrigin() ||
    TRUSTED_MAINTENANCE_ORIGINS.includes(parsedUrl.origin)
  );
};

// Gets the current page URL when this code is running in a browser.
const getCurrentLocationHref = (): string | undefined =>
  typeof window === 'undefined' ? undefined : window.location.href;

// Turns a string into a URL object, or returns nothing if the string is invalid.
const parseUrl = (
  url: string,
  baseUrl = MAINTENANCE_REDIRECT_URL,
): URL | undefined => {
  try {
    // The URL constructor can throw for malformed strings, so keep bad values harmless.
    return new URL(url, baseUrl);
  } catch {
    return undefined;
  }
};

// Gets the current page origin, such as http://localhost:8083.
const getCurrentOrigin = (): string | undefined => {
  const currentLocationHref = getCurrentLocationHref();

  return currentLocationHref
    ? parseUrl(currentLocationHref)?.origin
    : undefined;
};

// Checks the path after host and port.
const hasMaintenancePath = (url: URL): boolean =>
  // Match only /unavailable, not paths like /unavailable-report.
  normalizePath(url.pathname) === MAINTENANCE_UNAVAILABLE_PATH;

// Normalizes path strings before comparing them.
const normalizePath = (pathname: string): string =>
  // Allow /unavailable and /unavailable/ to count as the same path.
  pathname.replace(/\/+$/, '').toLowerCase() || '/';
